export interface ContactView {
  readonly id: string;
  readonly displayName: string;
  readonly email: string | null;
  readonly phone: string | null;
  readonly position: string | null;
  readonly sourceContactId: string | null;
  readonly hasPhoto: boolean;
}

export interface ProviderView {
  readonly id: string;
  readonly displayName: string;
  readonly email: string | null;
  readonly supplierReferenceId: string;
  readonly version: number;
  readonly hasLogo: boolean;
}
export interface ProviderContactView {
  readonly id: string;
  readonly displayName: string;
  readonly email: string | null;
  readonly phone: string | null;
  readonly position: string | null;
  readonly hasPhoto: boolean;
}
export interface ProviderQuote {
  readonly id: string;
  readonly title: string;
  readonly amount: string | null;
  readonly filename: string;
  readonly createdAt: string;
}

export type QuoteDecision = 'SENT_UNACCEPTED' | 'ACCEPTED' | 'EXCLUDED';

export interface CommercialSummary {
  readonly clientCount: number;
  readonly quoteCount: number;
  readonly unclassifiedCount: number;
  readonly staleCount: number;
  readonly prospectCount: number | null;
  readonly identifiedProspectCount: number;
  readonly prospects: readonly { id: string; label: string | null; quoteCount: number }[];
  readonly ratio: number | null;
  readonly page: number;
  readonly hasNext: boolean;
  readonly groups: readonly { id: string; label: string | null }[];
  readonly quotes: readonly {
    id: string;
    reference: string;
    clientLabel: string | null;
    status: string;
    state: string | null;
    outcome: QuoteDecision | null;
    recipientGroupId: string | null;
    needsReview: boolean;
  }[];
}

async function request<T>(path: string, csrf?: string, body?: unknown): Promise<T> {
  const response = await fetch('/api/directory/' + path, {
    method: body === undefined ? 'GET' : 'POST',
    credentials: 'same-origin',
    headers: {
      ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) throw new Error('DIRECTORY_REQUEST_FAILED');
  return response.json() as Promise<T>;
}

async function encodeFile(file: File, maxBytes: number): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  if (bytes.length > maxBytes) throw new Error('FILE_TOO_LARGE');
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += 8192)
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
  return btoa(binary);
}

export const partyDirectoryApi = {
  contacts(clientId: string): Promise<{
    contacts: readonly ContactView[];
    suggestions: readonly ContactView[];
  }> {
    return request('clients/' + encodeURIComponent(clientId) + '/contacts');
  },
  addContact(
    clientId: string,
    fields: {
      sourceContactId?: string;
      displayName?: string;
      email?: string | null;
      phone?: string | null;
      position?: string | null;
    },
    csrf: string,
  ): Promise<ContactView> {
    return request('clients/' + encodeURIComponent(clientId) + '/contacts', csrf, {
      ...fields,
      confirmed: true,
    });
  },
  updateContact(
    clientId: string,
    contactId: string,
    fields: Pick<ContactView, 'displayName' | 'email' | 'phone' | 'position'>,
    csrf: string,
  ): Promise<ContactView> {
    return request(
      `clients/${encodeURIComponent(clientId)}/contacts/${encodeURIComponent(contactId)}/edit`,
      csrf,
      fields,
    );
  },
  dismissContactSuggestion(
    clientId: string,
    sourceContactId: string,
    csrf: string,
  ): Promise<{ dismissed: true }> {
    return request(
      `clients/${encodeURIComponent(clientId)}/contacts/suggestions/${encodeURIComponent(sourceContactId)}/dismiss`,
      csrf,
      { confirmed: true },
    );
  },
  async saveContactPhoto(
    clientId: string,
    contactId: string,
    file: File,
    csrf: string,
  ): Promise<{ saved: true }> {
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type))
      throw new Error('IMAGE_INVALID');
    return request(
      `clients/${encodeURIComponent(clientId)}/contacts/${encodeURIComponent(contactId)}/photo`,
      csrf,
      { imageBase64: await encodeFile(file, 512 * 1024) },
    );
  },
  async saveProviderLogo(providerId: string, file: File, csrf: string): Promise<{ saved: true }> {
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type))
      throw new Error('IMAGE_INVALID');
    return request(`providers/${encodeURIComponent(providerId)}/logo`, csrf, {
      imageBase64: await encodeFile(file, 512 * 1024),
    });
  },
  providers(
    query: string,
    page = 0,
    mode: 'providers' | 'suppliers' = 'providers',
  ): Promise<{
    providers: readonly ProviderView[];
    candidates: readonly ProviderView[];
    providerCount: number;
    supplierCount: number;
  }> {
    return request('providers?q=' + encodeURIComponent(query) + '&page=' + page + '&mode=' + mode);
  },
  providerContacts(providerId: string): Promise<readonly ProviderContactView[]> {
    return request(`providers/${encodeURIComponent(providerId)}/contacts`);
  },
  saveProviderContact(
    providerId: string,
    contactId: string | null,
    fields: Pick<ProviderContactView, 'displayName' | 'email' | 'phone' | 'position'>,
    csrf: string,
  ): Promise<ProviderContactView> {
    return request(`providers/${encodeURIComponent(providerId)}/contacts`, csrf, {
      contactId,
      ...fields,
    });
  },
  async saveProviderContactPhoto(
    providerId: string,
    contactId: string,
    file: File,
    csrf: string,
  ): Promise<{ saved: true }> {
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type))
      throw new Error('IMAGE_INVALID');
    return request(
      `providers/${encodeURIComponent(providerId)}/contacts/${encodeURIComponent(contactId)}/photo`,
      csrf,
      {
        imageBase64: await encodeFile(file, 512 * 1024),
      },
    );
  },
  promoteProvider(supplierReferenceId: string, csrf: string): Promise<ProviderView> {
    return request('providers', csrf, { supplierReferenceId, confirmed: true });
  },
  demoteProvider(
    providerId: string,
    expectedVersion: number,
    csrf: string,
  ): Promise<{ requalified: true }> {
    return request(`providers/${encodeURIComponent(providerId)}/demote`, csrf, {
      expectedVersion,
      confirmed: true,
    });
  },
  confirmProviderEmail(
    providerId: string,
    expectedVersion: number,
    email: string,
    csrf: string,
  ): Promise<ProviderView> {
    return request(`providers/${encodeURIComponent(providerId)}/contact-email`, csrf, {
      expectedVersion,
      email,
      confirmed: true,
    });
  },
  providerQuotes(providerId: string): Promise<readonly ProviderQuote[]> {
    return request(`providers/${encodeURIComponent(providerId)}/quotes`);
  },
  async addProviderQuote(
    providerId: string,
    title: string,
    amount: string | null,
    file: File,
    csrf: string,
  ): Promise<{ id: string }> {
    if (file.type !== 'application/pdf') throw new Error('PDF_INVALID');
    if (file.size > 2_097_152) throw new Error('PDF_TOO_LARGE');
    const response = await fetch(
      `/api/directory/providers/${encodeURIComponent(providerId)}/quotes`,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/pdf',
          'X-CSRF-Token': csrf,
          'X-Quote-Title': encodeURIComponent(title),
          'X-Quote-Filename': encodeURIComponent(file.name),
          ...(amount ? { 'X-Quote-Amount': amount } : {}),
        },
        body: file,
      },
    );
    if (!response.ok) throw new Error('PDF_UPLOAD_FAILED');
    return response.json() as Promise<{ id: string }>;
  },
  commercialSummary(page: number, groupQuery = ''): Promise<CommercialSummary> {
    return request(
      'commercial-statistics?page=' + page + '&groupQuery=' + encodeURIComponent(groupQuery),
    );
  },
  classifyQuote(
    quoteReferenceId: string,
    outcome: QuoteDecision,
    existingRecipientGroupId: string | null,
    csrf: string,
  ): Promise<{ classified: true }> {
    return request('commercial-statistics/quotes/' + encodeURIComponent(quoteReferenceId), csrf, {
      outcome,
      existingRecipientGroupId,
      confirmed: true,
    });
  },
  inviteClientContact(
    clientId: string,
    contactId: string,
    email: string,
    givenName: string,
    familyName: string,
    csrf: string,
  ): Promise<{ status: string; expiresAt: string | null }> {
    return request(
      `clients/${encodeURIComponent(clientId)}/contacts/${encodeURIComponent(contactId)}/invitation`,
      csrf,
      {
        email,
        givenName,
        familyName,
        confirmed: true,
      },
    );
  },
  inviteProvider(
    providerId: string,
    email: string,
    givenName: string,
    familyName: string,
    csrf: string,
  ): Promise<{ status: string; expiresAt: string | null }> {
    return request(`providers/${encodeURIComponent(providerId)}/invitation`, csrf, {
      email,
      givenName,
      familyName,
      confirmed: true,
    });
  },
};
